import React, { useState, useEffect } from 'react'
import { FiSearch, FiPlus, FiMinus, FiPackage, FiTag } from 'react-icons/fi'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import api, { fetcher } from '../../utils/api'
import Spinner from '../../components/Spinner'
import { canModifyActivity } from '../../utils/activityUtils'

const CommodityActivityModal = ({ activity, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCommodities, setSelectedCommodities] = useState([])
  const [selectedAssignedCommodities, setSelectedAssignedCommodities] = useState([])
  const [loading, setLoading] = useState(false)
  const readOnly = !canModifyActivity(activity)

  // Fetch commodities using SWR
  const { data: allCommodities, isLoading: allCommoditiesLoading } = useSWR('commodities/', fetcher)
  const { data: activityCommodities, isLoading: activityCommoditiesLoading, mutate: mutateActivityCommodities } = useSWR(`activities/${activity?.id}/`, fetcher)

  // Ensure arrays are always arrays
  const safeAllCommodities = Array.isArray(allCommodities) ? allCommodities : []
  const safeActivityCommodities = Array.isArray(activityCommodities?.commodities) ? activityCommodities?.commodities : []

  // Filter commodities based on search term and exclude already assigned commodities
  const assignedCommodityIds = safeActivityCommodities?.map(ac => ac.commodity?.id || ac.id) || []
  const filteredCommodities = safeAllCommodities?.filter(commodity => {
    // Filter by search term
    const matchesSearch = searchTerm && searchTerm.trim() ? (
      commodity?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      commodity?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    ) : true

    // Exclude already assigned commodities
    const notAssigned = !assignedCommodityIds.includes(commodity?.id)

    return matchesSearch && notAssigned
  }) || []

  const handleAssignCommodity = async (commodity) => {
    if (readOnly) return
    setLoading(true)
    try {
      const response = await api.post(`activities/${activity?.id}/add_commodities/`, {
        commodity_ids: [commodity?.id]
      })
      if (response?.status === 200 || response?.status === 201) {
        toast.success(`${commodity?.name} assigned to activity`)
        mutateActivityCommodities() // Refresh the activity commodities list
      }
    } catch (error) {
      toast.error('Failed to assign commodity to activity')
    } finally {
      setLoading(false)
    }
  }

  const handleUnassignCommodity = async (commodity) => {
    if (readOnly) return
    setLoading(true)
    try {
      const response = await api.post(`activities/${activity?.id}/remove_commodities/`, {
        commodity_ids: [commodity?.id]
      })
      if (response?.status === 200 || response?.status === 201) {
        toast.success(`${commodity?.name} removed from activity`)
        mutateActivityCommodities() // Refresh the activity commodities list
      }
    } catch (error) {
      toast.error('Failed to remove commodity from activity')
    } finally {
      setLoading(false)
    }
  }

  const handleBulkAssign = async () => {
    if (readOnly) return
    if (selectedCommodities.length === 0) {
      toast.error('Please select commodities to assign')
      return
    }

    setLoading(true)
    try {
      const commodityIds = selectedCommodities.map(commodity => commodity?.id)
      const response = await api.post(`activities/${activity?.id}/add_commodities/`, {
        commodity_ids: commodityIds
      })
      if (response?.status === 200 || response?.status === 201) {
        toast.success(`${selectedCommodities.length} commodities assigned to activity`)
        setSelectedCommodities([])
        mutateActivityCommodities() // Refresh the activity commodities list
      }
    } catch (error) {
      toast.error('Failed to assign commodities to activity')
      console.error('Bulk assign error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleBulkRemove = async (commoditiesToRemove) => {
    if (readOnly) return
    if (commoditiesToRemove.length === 0) {
      toast.error('Please select commodities to remove')
      return
    }

    setLoading(true)
    try {
      const commodityIds = commoditiesToRemove.map(commodity => commodity?.id)
      const response = await api.post(`activities/${activity?.id}/remove_commodities/`, {
        commodity_ids: commodityIds
      })
      if (response?.status === 200 || response?.status === 201) {
        toast.success(`${commoditiesToRemove.length} commodities removed from activity`)
        mutateActivityCommodities() // Refresh the activity commodities list
      }
    } catch (error) {
      toast.error('Failed to remove commodities from activity')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectCommodity = (commodity) => {
    setSelectedCommodities(prev =>
      prev.find(c => c.id === commodity?.id)
        ? prev.filter(c => c.id !== commodity?.id)
        : [...prev, commodity]
    )
  }

  const handleSelectAll = () => {
    if (selectedCommodities.length === filteredCommodities.length) {
      setSelectedCommodities([])
    } else {
      setSelectedCommodities(filteredCommodities)
    }
  }

  const handleSelectAssignedCommodity = (commodity) => {
    setSelectedAssignedCommodities(prev =>
      prev.find(c => c.id === commodity?.id)
        ? prev.filter(c => c.id !== commodity?.id)
        : [...prev, commodity]
    )
  }

  const handleSelectAllAssigned = () => {
    if (selectedAssignedCommodities.length === safeActivityCommodities.length) {
      setSelectedAssignedCommodities([])
    } else {
      setSelectedAssignedCommodities(safeActivityCommodities.map(ac => ac.commodity || ac))
    }
  }

  if (allCommoditiesLoading || activityCommoditiesLoading || !Array.isArray(allCommodities)) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
          <Spinner />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full md:max-w-6xl relative max-h-[90vh] flex flex-col overflow-hidden">
        <div className='flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark'>
          <div>
            <h2 className='text-xl font-bold dark:text-white'>
              Manage Commodities - {activity?.name}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
              Assign and manage commodities for this activity
            </p>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-5 w-5" />
          </button>
        </div>

        {readOnly && (
          <div className="mx-6 mt-4 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
            This activity has ended. Commodity assignments can no longer be changed.
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          <div className="flex h-full">
            {/* Available Commodities */}
            <div className="flex-1 p-6 border-r border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Available Commodities ({filteredCommodities.length})
                </h3>
                {filteredCommodities.length > 0 && !readOnly && (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSelectAll}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      {selectedCommodities.length === filteredCommodities.length ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedCommodities.length > 0 && (
                      <button
                        onClick={handleBulkAssign}
                        disabled={loading}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {loading ? 'Assigning...' : `Assign ${selectedCommodities.length}`}
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search commodities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellowcolor focus:border-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white"
                  />
                </div>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredCommodities.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {searchTerm ? 'No commodities found matching your search' : 'No available commodities to assign'}
                  </div>
                ) : (
                  filteredCommodities.map(commodity => (
                    <div
                      key={commodity?.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${selectedCommodities.find(c => c.id === commodity?.id)
                        ? 'border-yellowcolor bg-yellowcolor/10 dark:bg-yellowcolor/20'
                        : 'border-gray-200 dark:border-border-dark hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      onClick={() => handleSelectCommodity(commodity)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {commodity?.name}
                          </h4>
                          {commodity?.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {commodity?.description}
                            </p>
                          )}
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mt-1">
                            <FiTag className="w-4 h-4 mr-1" />
                            ID: {commodity?.id}
                          </div>
                        </div>
                        {!readOnly && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAssignCommodity(commodity)
                          }}
                          disabled={loading}
                          className="ml-2 p-2 text-yellowcolor hover:bg-yellowcolor/10 dark:hover:bg-yellowcolor/20 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <FiPlus className="w-4 h-4" />
                        </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Assigned Commodities */}
            <div className="flex-1 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Assigned Commodities ({safeActivityCommodities.length})
                </h3>
                {safeActivityCommodities.length > 0 && !readOnly && (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSelectAllAssigned}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      {selectedAssignedCommodities.length === safeActivityCommodities.length ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedAssignedCommodities.length > 0 && (
                      <button
                        onClick={() => handleBulkRemove(selectedAssignedCommodities)}
                        disabled={loading}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                      >
                        {loading ? 'Removing...' : `Remove ${selectedAssignedCommodities.length}`}
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {safeActivityCommodities.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <FiPackage className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No commodities assigned to this activity</p>
                  </div>
                ) : (
                  safeActivityCommodities.map(activityCommodity => {
                    const commodity = activityCommodity.commodity || activityCommodity
                    const isSelected = selectedAssignedCommodities.find(c => c.id === commodity?.id)
                    return (
                      <div
                        key={commodity?.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${isSelected
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-200 dark:border-border-dark hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        onClick={() => handleSelectAssignedCommodity(commodity)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {commodity?.name}
                            </h4>
                            {commodity?.description && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {commodity?.description}
                              </p>
                            )}
                            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mt-1">
                              <FiTag className="w-4 h-4 mr-1" />
                              ID: {commodity?.id}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleUnassignCommodity(commodity)
                              }}
                              disabled={loading}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                            >
                              <FiMinus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default CommodityActivityModal
